// CANONICAL formatting helpers shared by both official report renderers.
//
// This file is the single source of truth. It is copied verbatim into each
// panel by `Scripts/sync_report_renderers.py`; do not edit the panel copies.
//
// The governing rule: a missing value stays missing. `Number(null)` is 0 and
// `Number("")` is 0, so every numeric formatter here rejects blank input
// *before* coercing it — otherwise an unrecorded mark renders as a real zero.

export const NOT_RECORDED = "Not recorded";

/** True only for values that carry no information. `0` is information. */
export const isBlank = value => value === null || value === undefined || value === "";

export const list = value => (Array.isArray(value) ? value : []);

/** A snapshot value may be a plain string or a structured {label,...} object. */
export const text = value => {
  if (isBlank(value)) return NOT_RECORDED;
  if (typeof value === "string") return value;
  return value.label || value.name || NOT_RECORDED;
};

/** Turn a stored key such as `digital_literacy` into `Digital Literacy`. */
export const humanise = value => {
  if (isBlank(value)) return NOT_RECORDED;
  return String(value)
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, character => character.toUpperCase());
};

/** Sentence-case a stored state such as `not_submitted` → `Not submitted`. */
export const words = value => {
  if (isBlank(value)) return NOT_RECORDED;
  const clean = String(value).replace(/[_-]+/g, " ").trim();
  return clean.charAt(0).toUpperCase() + clean.slice(1);
};

export const date = (value, options) => {
  if (isBlank(value)) return NOT_RECORDED;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return NOT_RECORDED;
  return parsed.toLocaleDateString(undefined, options || { day: "2-digit", month: "short", year: "numeric" });
};

/**
 * Format a number for display, or return `fallback` when nothing was recorded.
 * Trailing float noise is removed: 3.9000000000000004 renders as 3.9.
 */
export const number = (value, fallback = "—") => {
  if (isBlank(value)) return fallback;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return String(Math.round(numeric * 100) / 100);
};

/** A score out of a maximum, or a clear absence. Never "0 / 20" for a null. */
export const outOf = (value, maximum, fallback = "Not assessed") => {
  if (isBlank(value)) return fallback;
  const scored = number(value, null);
  if (scored === null) return fallback;
  const total = number(maximum, null);
  return total === null ? scored : `${scored} / ${total}`;
};

export const percentage = (value, fallback = NOT_RECORDED) => {
  if (isBlank(value)) return fallback;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return `${Math.round(numeric * 10) / 10}%`;
};

/** Growth uses a 1-5 scale and is never shown as a percentage. */
export const growthScore = value => {
  if (isBlank(value)) return "Not assessed";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "Not assessed";
  const normalized = numeric <= 1 ? numeric * 5 : numeric <= 5 ? numeric : numeric / 20;
  return `${normalized.toFixed(1)} / 5`;
};

/** Width of a score bar, in percent of the 1-5 scale. */
export const scoreBarWidth = value => {
  if (isBlank(value)) return 0;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  // Older Growth snapshots used either 0-1, 0-5, or 0-100 scales.
  return Math.max(0, Math.min(100, numeric <= 1 ? numeric * 100 : numeric <= 5 ? (numeric / 5) * 100 : numeric));
};

/** Evidence confidence is stored as a percentage; label it as one. */
export const confidence = value => {
  if (isBlank(value)) return "confidence not recorded";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "confidence not recorded";
  return `${Math.round(numeric)}% confidence`;
};

/** Filesystem-safe fragment for a PDF filename. */
export const safeName = value =>
  String(value || "report").replace(/[^a-z0-9]+/gi, "_").replace(/^_|_$/g, "");
